import chroma from 'chroma-js'

function chunkArray(array, chunkSize) {
  const arr = [...array]
  return Array(Math.ceil(arr.length / chunkSize))
    .fill()
    .map((_, i) => arr.slice(i * chunkSize, i * chunkSize + chunkSize))
}

export const legendColorScale = chroma.scale(['#ffffff', '#ffce0a', '#e74c04', '#000000']).domain([0, 0.52, 0.78, 1])
export const getLegendColors = (colorCount = 3) =>
  legendColorScale.colors(colorCount * 2 + 1).reduce((scale, color, idx) => (idx % 2 === 1 ? [...scale, color] : scale), [])
export const LEGEND_ZERO_GREY = '#f3f5f9'

function generateLegendBuckets(data, { bucketCount, includeZero }) {
  const generatedBucketsCount = includeZero ? bucketCount - 1 : bucketCount
  const legendColors = getLegendColors(generatedBucketsCount)
  // values are based on equal distribution ranges
  const values = [
    0,
    ...data
      .reduce((currentValues, { countries }) => {
        const countryValues = countries.reduce(
          (currentCountryValues, { revenuePerHour }) => [...currentCountryValues, ...revenuePerHour.map(({ value }) => value || 0)],
          [],
        )
        return [...currentValues, ...countryValues]
      }, [])
      .filter(val => val !== 0),
  ].sort((a, b) => a - b)
  const chunkSize = Math.ceil(values.length / generatedBucketsCount)
  const equalChunks = chunkArray(values, chunkSize)

  return equalChunks.map((chunk, index) => ({
    color: index === 0 ? LEGEND_ZERO_GREY : legendColors[index - 1],
    min: chunk[0],
    max: chunk[chunk.length - 1],
  }))
}

/**
 * @typedef {Object} HourlyRevenue
 * @property {number} hour
 * @property {Array<Object<HourlyRevenue>} value
 */

/**
 * @typedef {Object} CountryHourlyRevenue
 * @property {string} countryId
 * @property {Array<HourlyRevenue>} revenuePerHour
 */

/**
 * @typedef {Object} CountriesHourlyRevenueByDate
 * @property {string} date
 * @property {Array<CountryHourlyRevenue>} countries
 */

/**
 *
 * @typedef {Object} LegendItem
 * @property {string} color hex/rgba color
 * @property {number} min
 * @property {number | null} max if null it does not have top limit
 */

/**
 * Generates a legend with colors and ranges based in HourslySalesData
 * @param {Array<CountriesHourlyRevenueByDate>} data
 * @param {Object?} options
 * @param {number} options.bucketCount number of legend items to return
 * @param {boolean} options.includeZero specifies if first item should be of (0,0) range
 * @returns {Array<LegendItem>}
 */
const generateLegend = (data, options = {}) => {
  if (data.length === 0) return []

  const mergedOptions = {
    bucketCount: 5,
    includeZero: false,
    ...options,
  }

  const generatedBuckets = generateLegendBuckets(data, mergedOptions)
  return mergedOptions.includeZero ? [{ color: LEGEND_ZERO_GREY, min: 0, max: 0 }, ...generatedBuckets] : generatedBuckets
}

export default generateLegend
